import * as React from "react";

import { I18n } from "react-redux-i18n";
import { getTestAttributes } from "@common/utils/tests";
import { Tabs } from '@common/components/tabs';
import cx from "classnames";

import { TextEditor } from "@common/components/text-editor";
import { ToolbarCustomButton } from "@common/components/text-editor/text-editor.types";

import "./editor.styles.scss";

type ElementTestArgs = {
  module?: string;
  description?: string;
  element?: string;
  id?: string;
};

export type EditorProps = {
  getId?: {
    textArea?: ElementTestArgs;
  };
  value?: string;
  defaultValue?: string;
  onBlur?: (value: string) => void;
  onChange?: (value: string) => void;
  classes?: {
    root?: string;
    tabs?: string;
    workArea?: string;
    workAreaInternal?: string;
    textarea?: string;
  };
  toolbarCustomButtons?: Array<ToolbarCustomButton>
};

export const Editor: React.FC<EditorProps> = (props) => {
  const TAB_TYPE = {
    editor: 'editor',
    html: 'html',
  };
  const { onBlur, onChange, value, defaultValue, classes, toolbarCustomButtons, getId } = props;
  const [currentTab, setCurrentTab] = React.useState(TAB_TYPE.editor);
  const localModuleName = 'email-template';

  React.useEffect(() => {
    document.body.onselectstart = null;

    return () => {
      document.body.onselectstart = function () {
        return false;
      };
    };
  }, []);


  const handleHtmlTextareaBlur = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.currentTarget;

      if (onBlur) {
        onBlur(value);
      }
    },
    [onBlur]
  );

  const handleHtmlTextareaChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.currentTarget;

      if (onChange) {
        onChange(value);
      }
    },
    [onChange]
  );

  const tabs = React.useMemo(() => {
    return [
      {
        ...getTestAttributes(localModuleName, 'html-tab', 'tab'),
        label: 'HTML',
        value: TAB_TYPE.html
      },
      {
        ...getTestAttributes(localModuleName, 'editor-tab', 'tab'),
        label: I18n.t('EDITOR'),
        value: TAB_TYPE.editor
      },
    ];
  }, []);

  return (
    <div className={cx("EmailTemplateEditor", classes?.root)}>
      <Tabs 
        value={currentTab}
        items={tabs}
        className={cx("EmailTemplateEditor__tabs", classes?.tabs)}
        onChange={setCurrentTab}
      />
      {currentTab === TAB_TYPE.editor && (
        <TextEditor 
          classes={{
            workArea: cx('EmailTemplateEditor__editor__work-area', classes?.workAreaInternal),
          }}
          className={cx("EmailTemplateEditor__editor", classes?.workArea)}
          value={value}
          defaultValue={typeof value === 'string' ? undefined : defaultValue}
          onChange={onChange}
          onBlur={onBlur}
          toolbarCustomButtons={toolbarCustomButtons}
        />
      )}
      {currentTab === TAB_TYPE.html && (
        <textarea 
          {...getTestAttributes(
            getId?.textArea?.module || localModuleName, 
            getId?.textArea?.description || 'editor', 
            getId?.textArea?.element || 'input',
            getId?.textArea?.id
          )}
          value={value}
          defaultValue={typeof value === 'string' ? undefined : defaultValue}
          className={cx("EmailTemplateEditor__html-textarea", classes?.textarea)}
          onBlur={handleHtmlTextareaBlur}
          onChange={handleHtmlTextareaChange}
        />
      )}
    </div>
  );
};
